import React from "react";
import { Box, Typography, Button, Grid, Stack, useMediaQuery } from "@mui/material";
import { minWidth, styled } from "@mui/system";
import WhatsappConnectImg from "./whatsapp-connect.png";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import StarImg from "./star_icon.png";
import googleForm from "./Google form.png";
import googleSheet from "./Google sheet.png";
import trelloImg from "./Trello.png";
import activeCampaign from "./Active campaign.png";
import clickFunnel from "./clickfunnel.png";
import zoomWebinar from "./Zoom webinar.png";
import zohoDesk from "./zoho-desk.png";
import hubSpot from "./Hub spot.png";
import pipeDrive from "./pipedrive.png";

const apps = [
  { name: "Google Forms", icon: googleForm },
  { name: "Google Sheets", icon: googleSheet },
  { name: "Trello", icon: trelloImg },
  { name: "Active Campaign", icon: activeCampaign },
  { name: "ClickFunnel", icon: clickFunnel },
  { name: "Zoom Webinar", icon: zoomWebinar },
  { name: "Zoho Desk", icon: zohoDesk },
  { name: "Hubspot", icon: hubSpot },
  { name: "Pipedrive", icon: pipeDrive },
];

// Desktop specific slices
const appsRow1 = apps.slice(0, 5); // Google Forms to ClickFunnel
const appsRow2 = apps.slice(5);     // Remaining apps

// Mobile specific app grouping for Grid layout
const mobileAppGroups = [
  apps.slice(0, 2),
  apps.slice(2, 4),
  apps.slice(4, 6),
  apps.slice(6, 8),
  apps.slice(8, 9),
];

const theme = createTheme();

const AppButton = styled(Button)(({ theme }) => ({
  padding: "4px 4px", //desktop padding
  border: "1px solid rgb(212, 225, 239)",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: "bold",
  color: "#333",
  backgroundColor: "#fff",
  fontSize: "0.9rem", // desktop 
  "&:hover": {
    backgroundColor: "#f0f0f0",
  },
  flexShrink: 0,
  flexGrow: 0,
  width: '19%', // Default desktop width for 5 buttons per row
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "5px",

  [theme.breakpoints.down("sm")]: {
    // width: '100%', // Full width inside Grid item (Grid item itself controls 50%)
    width: 'fit-content',
    fontSize: "0.75rem", // Mobile
    padding: "8px 8px", // Mobile padding
    marginBottom: '10px'
  },
}));

const MoreIntegrationButton = styled(Button)(({ theme }) => ({
  padding: "2px 6px", // desktop padding
  borderRadius: "15px",
  fontWeight: "bold",
  color: "#fff",
  backgroundColor: "#4caf50",
  textTransform: "none",
  whiteSpace: "nowrap",
  minWidth: "fit-content",
  fontSize: "0.9rem", // Desktop 
  "&:hover": {
    backgroundColor: "#45a049",
  },
  width: '19%',
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "2px",
  border: "1px solid #4caf50",

  [theme.breakpoints.down("sm")]: {
    whiteSpace: "normal",
    minWidth: "unset",
    width: '100%',
    height: 'auto',
    padding: "8px 8px",
    fontSize: "0.75rem",
    borderRadius: "8px",
  },
}));


const ConnectionApps = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <ThemeProvider theme={theme}>
      <Stack
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          padding: "20px",
          my: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            backgroundColor: "#f5faff",
            padding: isMobile ? "20px" : "30px",
            borderRadius: "10px",
            boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
            width: { xl: "72%", lg: "92%", sm: "96%", xs: "100%" },
            position: "relative",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          {/* "Included in All Plans" Button - Top Right */}
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              borderRadius: "0 10px 0 10px",
              backgroundColor: "#ff9800",
              color: "#fff",
              textTransform: "none",
              fontWeight: "bold",
              fontSize: "14px",
              zIndex: 1,
              padding: "8px 15px",
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                transform: "skewX(-45deg)",
                animation: "lightSweep 2s infinite",
              },
              "@keyframes lightSweep": {
                "0%": { left: "-100%" },
                "50%": { left: "100%" },
                "100%": { left: "-100%" },
              },
            }}
          >
            <img
              src={StarImg}
              alt="Star Icon"
              style={{ width: '18px', height: '18px', marginBottom: "2px", filter: 'brightness(0) invert(1)' }}
            />
            Included in All Plans
          </Button>

          {/* Left Section - Image */}
          <Box
            sx={{
              flexShrink: 0,
              marginRight: isMobile ? "0" : "20px",
              marginBottom: isMobile ? "15px" : "0",
              display: "flex",
              justifyContent: isMobile ? "flex-start" : "center",
              alignItems: "center",
              width: isMobile ? "100%" : "auto",
              marginTop: { sm: "0px", xs: "-12px" }
            }}
          >
            <img
              src={WhatsappConnectImg}
              alt="WhatsApp Logo"
              style={{
                width: isMobile ? "95px" : "200px",
                height: isMobile ? "95px" : "200px",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Right Section - Content and Buttons */}
          <Box
            sx={{
              flexGrow: 1,
              flexDirection: "column",
              alignItems: isMobile ? "flex-start" : "flex-start",
              textAlign: isMobile ? "left" : "left",
              width: isMobile ? "100%" : "auto",
              marginLeft: { xs: "0", sm: "0", md: "4%" },
            }}
          >
            {/* Heading */}
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                color: "#333",
                textAlign: isMobile ? "left" : "left",
                marginBottom: "5px",
                fontSize: { xs: "24px", sm: "20px", md: "28px" },
                alignItems: { sm: "none", xs: "justify" }
              }}
            >
              Connect WhatsApp Into{" "}
              {isMobile && <br />} {/* Line break only on mobile */}
              <span style={{ color: "#4caf50" }}>5000+ Apps</span>
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "16px" },
                color: "#333",
                marginBottom: "20px",
                // Changed: Text align to left on mobile
                textAlign: isMobile ? "left" : "left",
                maxWidth: "600px",
              }}
            >
              Connector helps you to integrate WhatsApp into 5000+ Apps quickly{" "}
              <span style={{ color: "#007bff", cursor: "pointer" }}>
                Read More
              </span>
            </Typography>

            {isMobile ? (
              // Mobile Layout using Grid
              <Stack
                display={'flex'}
                direction="row"
                flexWrap="wrap"
                justifyContent="flex-start"
                gap={{ xs: '5%', md: '1%' }}
                sx={{
                  width: "100%",
                  marginBottom: "10px",
                }}
              >
                {apps.map((app, index) => (
                  <AppButton key={index} variant="outlined">
                    {app.icon && (
                      <Box sx={{
                         width: { xs: '15px', md: "20px" }, // Desktop size
                            height: { xs: '15px', md: "20px" }, // Desktop size
                      }}>
                        <img
                          src={app.icon}
                          alt={`${app.name} Icon`}
                          style={{
                           height: '100%',
                           width: '100%',
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                    )}
                    {app.name}
                  </AppButton>
                ))}
                <MoreIntegrationButton>
                  5000+ More Integrations →
                </MoreIntegrationButton>
              </Stack>
            ) : (
              // Desktop Layout using Stacks
              <>
                {/* App Buttons Row 1 (first 5 apps) */}
                <Stack
                  display={'flex'}
                  direction="row"
                  flexWrap="wrap"
                  justifyContent="space-between"
                  gap={'1%'}
                  sx={{
                    width: "100%",
                    marginBottom: "10px",
                  }}
                >
                  {appsRow1.map((app, index) => (
                    <AppButton key={index} variant="outlined">
                      {app.icon && (
                        <img
                          src={app.icon}
                          alt={`${app.name} Icon`}
                          style={{
                            width: "20px", // Desktop size
                            height: "20px", // Desktop size
                            objectFit: "contain",
                          }}
                        />
                      )}
                      {app.name}
                    </AppButton>
                  ))}
                </Stack>

                {/* App Buttons Row 2 (remaining apps and "5000+ More Integrations" button) */}
                <Stack
                  display={'flex'}
                  direction="row"
                  flexWrap="wrap"
                  justifyContent="flex-start"
                  gap={'1%'}
                  sx={{
                    width: "100%",
                    marginBottom: "10px",
                  }}
                >
                  {appsRow2.map((app, index) => (
                    <AppButton key={index} variant="outlined">
                      {app.icon && (
                        <img
                          src={app.icon}
                          alt={`${app.name} Icon`}
                          style={{
                            width: "20px", // Desktop size
                            height: "20px", // Desktop size
                            objectFit: "contain",
                          }}
                        />
                      )}
                      {app.name}
                    </AppButton>
                  ))}
                  {/* "5000+ More Integrations" button for desktop */}
                  <MoreIntegrationButton>
                    5000+ More Integrations →
                  </MoreIntegrationButton>
                </Stack>
              </>
            )}

          </Box>
        </Box>
      </Stack>
    </ThemeProvider>
  );
};

export default ConnectionApps;